import { type BoardView, type TaskView } from '@apm/contracts';

/** Cột sắp xếp được của bảng phẳng — APM-BOARD-b. */
export const LIST_SORTS = [
  'key',
  'title',
  'assignee',
  'status',
  'due',
  'timeline',
  'progress',
  'priority',
  'points',
] as const;
export type ListSort = (typeof LIST_SORTS)[number];

export interface ListFilter {
  /** Khớp mã hoặc tiêu đề, không phân biệt hoa thường. `''` = không lọc. */
  readonly text: string;
  /** `null` = mọi người. `''` = **chưa giao ai** — khác hẳn "mọi người". */
  readonly assigneeId: string | null;
  /** `null` = mọi cột. */
  readonly statusId: string | null;
  /** Chỉ việc quá hạn và chưa xong. */
  readonly overdueOnly: boolean;
}

export const EMPTY_FILTER: ListFilter = {
  text: '',
  assigneeId: null,
  statusId: null,
  overdueOnly: false,
};

/**
 * Board → một hàng phẳng cho mỗi task.
 *
 * Cách nhìn Danh sách đọc **cùng một dữ liệu** với Board và Gantt (spec
 * `APM-BOARD-b`): ba cách vẽ của cùng một tập cột và task. Gọi một endpoint
 * riêng cho nó là mở đường cho ba màn hình nói ba chuyện khác nhau về cùng một
 * sprint — đúng loại lệch không ai thấy cho tới lúc quá muộn.
 */
export function flatten(view: BoardView): TaskView[] {
  return view.columns.flatMap((column) => column.tasks);
}

/**
 * Task đã quá hạn — và **chưa xong**.
 *
 * Bỏ vế thứ hai thì mọi việc giao muộn trong lịch sử dự án đều ở lại trong bộ
 * lọc mãi mãi, và "việc quá hạn" thành một danh sách chỉ dài ra chứ không bao
 * giờ ngắn lại. Nó là danh sách để hành động, không phải một sổ ghi nợ.
 */
export function isOverdue(task: TaskView, today: string): boolean {
  return task.dueDate !== null && task.dueDate < today && task.statusCategory !== 'done';
}

export function filterTasks(
  tasks: readonly TaskView[],
  filter: ListFilter,
  today: string,
): TaskView[] {
  const needle = filter.text.trim().toLowerCase();

  return tasks.filter((task) => {
    if (needle && !`${task.key} ${task.title}`.toLowerCase().includes(needle)) return false;
    // `''` là "chưa giao ai", `null` là "không lọc" — hai câu khác nhau, và gộp
    // chúng lại thì đúng cái đống việc vô chủ trở thành thứ không lọc ra được.
    if (filter.assigneeId !== null && (task.assigneeId ?? '') !== filter.assigneeId) return false;
    if (filter.statusId !== null && task.statusId !== filter.statusId) return false;
    if (filter.overdueOnly && !isOverdue(task, today)) return false;
    return true;
  });
}

/**
 * Sắp xếp ổn định, và **giá trị trống luôn xuống cuối**.
 *
 * Task không có hạn hay không có điểm là chuyện thường. Để `null` trôi lên đầu
 * khi sắp tăng dần thì màn hình đầu tiên toàn ô trống, và người dùng kết luận
 * bảng hỏng — trong khi thứ họ hỏi ("việc nào tới hạn sớm nhất") nằm ở dưới.
 */
export function sortTasks(
  tasks: readonly TaskView[],
  sort: ListSort,
  descending: boolean,
): TaskView[] {
  const direction = descending ? -1 : 1;

  return [...tasks].sort((a, b) => {
    const [left, right] = [valueOf(a, sort), valueOf(b, sort)];

    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;

    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * direction;
    }
    return String(left).localeCompare(String(right), 'vi') * direction;
  });
}

/** Thấp → gấp. Dùng cho sắp xếp; màu thì ở `priorityToneOf`. */
const PRIORITY_RANK: Record<string, number> = { low: 0, medium: 1, high: 2, urgent: 3 };

function valueOf(task: TaskView, sort: ListSort): string | number | null {
  switch (sort) {
    case 'key':
      // Theo **số**, không theo chuỗi: `PAY-10` phải đứng sau `PAY-9`, còn so
      // chuỗi thì nó đứng ngay sau `PAY-1`.
      return task.number;
    case 'title':
      return task.title;
    case 'status':
      return task.statusName;
    case 'assignee':
      return task.assigneeName;
    case 'due':
      return task.dueDate;
    case 'timeline':
      // Theo **ngày bắt đầu**: cột này trả lời "việc chạy khi nào", và một dải
      // thời gian được đọc từ đầu dải. Việc chưa có ngày bắt đầu rơi xuống cuối
      // qua `nulls last` chung của `sortTasks`.
      return task.startDate;
    case 'progress':
      return task.progressPct;
    case 'priority':
      // Theo **thứ bậc**, không theo tên: so chuỗi thì `urgent` xuống cuối vì
      // chữ `u`, tức đúng thứ cần thấy trước lại nằm sau cùng.
      return PRIORITY_RANK[task.priority] ?? 0;
    case 'points':
      return task.storyPoints;
  }
}

// ─────────────────────────────────────────────────────────────────────────────

/** Trục gom nhóm của bảng Danh sách — APM-LIST-c. */
export const GROUP_BYS = ['status', 'assignee', 'none'] as const;
export type GroupBy = (typeof GROUP_BYS)[number];

export interface TaskGroup {
  /** Khoá ổn định để nhớ nhóm nào đang gập. `''` = nhóm "chưa có". */
  readonly key: string;
  readonly label: string;
  readonly tasks: readonly TaskView[];
}

/**
 * Gom hàng thành nhóm — APM-LIST-c.
 *
 * ## Vì sao nhóm rỗng vẫn hiện (khi gom theo cột)
 *
 * Một cột không có việc nào **là** thông tin: "Chờ duyệt trống" khác hẳn "Chờ
 * duyệt không tồn tại". Bỏ nhóm rỗng đi thì bảng nói dối về hình dạng quy
 * trình, và người dùng đi tìm một cột họ biết là có.
 *
 * Với "phụ trách" thì ngược lại — chỉ dựng nhóm cho người **thật sự có việc**:
 * danh sách mọi thành viên dự án kể cả người không được giao gì là một bảng
 * dài ra vì lý do không ai quan tâm.
 *
 * ## Thứ tự
 *
 * Cột theo `position` (đúng thứ tự trái-sang-phải của board — hai màn hình
 * phải kể cùng một câu chuyện). Người theo tên, và **"chưa giao ai" xuống
 * cuối**: nó không phải một người, nên xếp nó lẫn vào danh sách tên là sai.
 */
export function groupTasks(
  tasks: readonly TaskView[],
  groupBy: GroupBy,
  columns: readonly { id: string; name: string; position: number }[],
  unassignedLabel: string,
  allLabel: string,
): TaskGroup[] {
  if (groupBy === 'none') return [{ key: '', label: allLabel, tasks: [...tasks] }];

  const buckets = new Map<string, TaskView[]>();
  const keyOf = (task: TaskView): string =>
    groupBy === 'status' ? task.statusId : (task.assigneeId ?? '');

  for (const task of tasks) {
    const key = keyOf(task);
    buckets.set(key, [...(buckets.get(key) ?? []), task]);
  }

  if (groupBy === 'status') {
    return [...columns]
      .sort((a, b) => a.position - b.position)
      .map((column) => ({
        key: column.id,
        label: column.name,
        tasks: buckets.get(column.id) ?? [],
      }))
      .concat(
        // Việc ở backlog không thuộc cột nào của board — nó vẫn phải có chỗ,
        // nếu không bảng đếm 35 việc mà chỉ vẽ ra 30.
        [...buckets.entries()]
          .filter(([key]) => !columns.some((column) => column.id === key))
          .map(([key, rows]) => ({
            key,
            label: rows[0]?.statusName ?? unassignedLabel,
            tasks: rows,
          })),
      );
  }

  return [...buckets.entries()]
    .map(([key, rows]) => ({
      key,
      label: key === '' ? unassignedLabel : (rows[0]?.assigneeName ?? unassignedLabel),
      tasks: rows,
    }))
    .sort((a, b) => {
      if (a.key === '') return 1;
      if (b.key === '') return -1;
      return a.label.localeCompare(b.label, 'vi');
    });
}

/**
 * Cột mà một việc **chuyển tới được** — sửa lỗi người dùng báo 2026-10-08.
 *
 * Mỗi sprint có bộ cột riêng (APM-BOARD-a1), cộng một bộ cột ngoài sprint
 * (`sprintId === null`) hiện ở mọi sprint — và bộ nào cũng mặc định tên "Sẵn
 * sàng / Đang làm / Hoàn thành". Board trả cả hai bộ (và ở "Cả dự án" là cột
 * của **mọi** sprint đang có việc), nên một menu đổ thẳng `board.columns` ra là
 * mỗi tên hiện hai, ba lần, không gì phân biệt.
 *
 * Việc trong sprint S chỉ nên tới: cột của S, và cột ngoài sprint. Cột của
 * sprint khác bị loại — chọn nó là để lại một việc mang `sprintId` của S nằm
 * trong cột của sprint kia, và board của cả hai sprint đều không vẽ nó ra.
 *
 * Cột **đang chứa** việc luôn được giữ, kể cả khi lệch phạm vi (dữ liệu cũ):
 * menu phải đánh dấu được chỗ việc đang đứng.
 *
 * Thứ tự: cột của sprint trước, cột ngoài sprint sau — cùng thứ tự hai khung
 * của board, và để tiêu đề nhóm trong menu không bị cắt vụn.
 */
export function columnsForTask<T extends { id: string; sprintId: string | null }>(
  columns: readonly T[],
  task: { readonly sprintId: string | null; readonly statusId: string },
): T[] {
  const reachable = columns.filter(
    (column) =>
      column.sprintId === null || column.sprintId === task.sprintId || column.id === task.statusId,
  );
  // `sort` của JS ổn định: trong mỗi nhóm, thứ tự `position` từ server giữ nguyên.
  return reachable.sort((a, b) => Number(a.sprintId === null) - Number(b.sprintId === null));
}

/**
 * Board này có **cả** cột của sprint lẫn cột ngoài sprint không.
 *
 * Chỉ khi có cả hai thì mới cần gắn nhãn phân biệt. Dự án Waterfall (toàn cột
 * ngoài sprint) mà cũng dán "Cột ngoài sprint" lên mọi thứ là nói về một khái
 * niệm dự án ấy không có.
 */
export function hasBothColumnScopes(
  columns: readonly { readonly sprintId: string | null }[],
): boolean {
  return (
    columns.some((column) => column.sprintId === null) &&
    columns.some((column) => column.sprintId !== null)
  );
}
